import {
    AccountLink,
    AccountLinksApi,
    GeneratedLinkingCode,
    RequestApproveAccountLink,
    RequestGenerateLinkingCode,
    RequestUpdateAccountLink,
    RequestValidateLinkingCode,
} from 'oas/datasharing';
import { UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { useApiMutation } from '../../use-api-mutation';
import { API_NAMES } from '../../constants';

// The response interceptor in use-api-config.tsx returns AxiosErrors instead of rejecting,
// which causes .data to return undefined and swallows the error. Re-throw if that happens.
const rethrowSwallowedError = <T,>(result: T): T => {
    if (result && typeof result === 'object' && 'isAxiosError' in result) {
        throw result;
    }
    return result;
};

export const useValidateLinkingCode = (): UseMutationResult<AccountLink, unknown, RequestValidateLinkingCode> => {
    return useApiMutation(
        async (attr, apiConfig, axiosInstance) =>
            rethrowSwallowedError(await new AccountLinksApi(apiConfig, '', axiosInstance).validateLinkingCode(attr))
                ?.data,
        {},
        [],
        'v6',
        API_NAMES.DATASHARING,
    );
};

export const useGenerateLinkingCode = (): UseMutationResult<
    GeneratedLinkingCode,
    unknown,
    RequestGenerateLinkingCode
> => {
    const queryClient = useQueryClient();

    return useApiMutation(
        async (attr, apiConfig, axiosInstance) =>
            rethrowSwallowedError(await new AccountLinksApi(apiConfig, '', axiosInstance).generateLinkingCode(attr))
                ?.data,
        {
            onSettled: () => {
                queryClient.invalidateQueries({
                    queryKey: ['datasharing', 'account-links'],
                });
            },
        },
        [],
        'v6',
        API_NAMES.DATASHARING,
    );
};

export const useApproveAccountLink = (): UseMutationResult<
    AccountLink,
    unknown,
    { accountLinkId: string } & RequestApproveAccountLink
> => {
    const queryClient = useQueryClient();

    return useApiMutation(
        async (attr, apiConfig, axiosInstance) => {
            const { accountLinkId, ...approveRequest } = attr;
            return rethrowSwallowedError(
                await new AccountLinksApi(apiConfig, '', axiosInstance).approveAccountLink(
                    accountLinkId,
                    approveRequest,
                ),
            )?.data;
        },
        {
            onSettled: () => {
                queryClient.invalidateQueries({
                    queryKey: ['datasharing', 'account-links'],
                });
            },
        },
        [],
        'v6',
        API_NAMES.DATASHARING,
    );
};

export const useUpdateAccountLink = (): UseMutationResult<
    AccountLink,
    unknown,
    { accountLinkId: string } & RequestUpdateAccountLink
> => {
    const queryClient = useQueryClient();

    return useApiMutation(
        async (attr, apiConfig, axiosInstance) => {
            const { accountLinkId, ...updateRequest } = attr;
            return rethrowSwallowedError(
                await new AccountLinksApi(apiConfig, '', axiosInstance).updateAccountLink(accountLinkId, updateRequest),
            )?.data;
        },
        {
            // On success, update the specific account link in the cache to reflect changes immediately in the UI without waiting for a refetch.
            onSuccess: (data) => {
                if (!data) {
                    return;
                }
                queryClient.setQueryData<AccountLink[]>(['datasharing', 'account-links'], (old) =>
                    old?.map((link) => (link.id === data.id ? data : link)),
                );
            },
            onSettled: (_data, _error, variables) => {
                queryClient.invalidateQueries({
                    queryKey: ['datasharing', 'account-links'],
                });
                queryClient.invalidateQueries({
                    queryKey: ['datasharing', 'account-links', variables.accountLinkId],
                });

                // A rename changes the tag that mirrors the link name and the activity labels
                // shown in the sidebar, so refresh those caches when the payload touches the name.
                const renamed = variables.sharer_link_name !== undefined || variables.recipient_link_name !== undefined;
                if (renamed) {
                    queryClient.invalidateQueries({ queryKey: ['tags', 'tag_type'] });
                    queryClient.invalidateQueries({ queryKey: ['activities'] });
                }
            },
        },
        [],
        'v6',
        API_NAMES.DATASHARING,
    );
};

export const useDeleteAccountLink = (): UseMutationResult<void, unknown, string> => {
    const queryClient = useQueryClient();

    return useApiMutation(
        async (accountLinkId, apiConfig, axiosInstance) => {
            rethrowSwallowedError(
                await new AccountLinksApi(apiConfig, '', axiosInstance).deleteAccountLink(accountLinkId),
            );
        },
        {
            onSettled: () => {
                queryClient.invalidateQueries({
                    queryKey: ['datasharing', 'account-links'],
                });
            },
        },
        [],
        'v6',
        API_NAMES.DATASHARING,
    );
};
